import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Target, Zap, BarChart3, Play, Star, Shield, Bell, ArrowRight } from 'lucide-react';
import api from '../utils/api';
import useAuthStore from '../store/authStore';

const Home = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (isAuthenticated && (user?.role === 'instructor' || user?.role === 'admin')) {
      fetchPendingCount();
    }
  }, [isAuthenticated, user]);

  const fetchCourses = async () => {
    try {
      const data = await api.get('/courses?limit=6');
      if (data.success) {
        setCourses(data.data);
      }
    } catch (error) {
      console.error('获取课程失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const data = await api.get('/authorizations/instructor/pending-count');
      if (data.success) {
        setPendingCount(data.count);
      }
    } catch (error) {
      // 静默处理
    }
  };

  return (
    <div className="space-y-12">
      {/* 讲师通知横幅 */}
      {isAuthenticated && (user?.role === 'instructor' || user?.role === 'admin') && pendingCount > 0 && (
        <Link to="/instructor?tab=applications" className="block bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Bell className="h-6 w-6" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingCount}
                </span>
              </div>
              <div>
                <p className="font-semibold text-lg">您有 {pendingCount} 条新的观看申请待审核</p>
                <p className="text-amber-100 text-sm">点击查看详情并处理</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5" />
          </div>
        </Link>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl overflow-hidden text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              开启您的学习之旅
            </h1>
            <p className="text-lg md:text-xl text-primary-100 mb-8">
              精选优质课程，专业讲师授课，随时随地学习新知识，提升自我价值
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/courses" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                浏览课程
              </Link>
              <Link to="/register" className="bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-800 transition-colors border border-primary-500">
                立即注册
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 为什么选择我们 */}
      <section>
        <h2 className="text-2xl font-bold text-center mb-8">为什么选择我们</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="card p-6 text-center border-l-4 border-orange-400">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="h-6 w-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">结果导向，拒绝空谈</h3>
            <p className="text-gray-600">不割韭菜，只交付结果。每一门课程都是经过实战检验的干货</p>
          </div>
          <div className="card p-6 text-center border-l-4 border-purple-400">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">AI赋能，效率翻倍</h3>
            <p className="text-gray-600">AI智能辅助学习，精准答疑，让学习效果加乘</p>
          </div>
          <div className="card p-6 text-center border-l-4 border-blue-400">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">系统教学，全程陪跑</h3>
            <p className="text-gray-600">从0到1系统化课程体系，讲师全程指导，学完即能上手</p>
          </div>
        </div>
      </section>

      {/* 实力见证 */}
      <section className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-8 text-white">
        <h2 className="text-2xl font-bold text-center mb-8">实力见证</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl font-bold mb-2">{courses.length}+</div>
            <p className="text-primary-100 text-sm">实操课程</p>
          </div>
          <div>
            <div className="text-4xl font-bold mb-2">100%</div>
            <p className="text-primary-100 text-sm">实战交付</p>
          </div>
          <div>
            <div className="text-4xl font-bold mb-2">
              <Zap className="h-8 w-8 inline-block" />
            </div>
            <p className="text-primary-100 text-sm">AI智能驱动</p>
          </div>
          <div>
            <div className="text-4xl font-bold mb-2">0</div>
            <p className="text-primary-100 text-sm">基础也能学会</p>
          </div>
        </div>
      </section>

      {/* 热门课程 */}
      <section>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">热门课程</h2>
          <Link to="/courses" className="text-primary-600 hover:text-primary-700 font-medium">
            查看全部 →
          </Link>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            暂无课程
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Link key={course._id} to={`/courses/${course._id}`} className="card overflow-hidden hover:shadow-md transition-shadow">
                <div className="aspect-video bg-gray-200 relative">
                  {course.thumbnail ? (
                    <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <Play className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 flex gap-1">
                    {course.accessType === 'authorized' && (
                      <span className="bg-purple-600/90 text-white text-xs px-2 py-0.5 rounded flex items-center">
                        <Shield className="h-3 w-3 mr-0.5" />授权
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {course.stats?.totalLessons || 0} 课时
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2">{course.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{course.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-600">
                        {course.stats?.rating?.toFixed(1) || '5.0'}
                      </span>
                    </div>
                    <div className="text-right">
                      {course.accessType === 'authorized' ? (
                        <span className="text-sm text-purple-600 font-medium">授权观看</span>
                      ) : (
                        <span className="text-lg font-bold text-red-600">¥{course.price?.individual || 999}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
